import { Ban, Calendar, Check, Clock, Phone, Star, Users, X } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { apiErrorMessage } from '../api/client'
import { connectionsApi, engagementsApi, profileApi, ratingsApi, trustApi } from '../api/endpoints'
import { useAuth } from '../auth/AuthContext'
import { ErrorMessage } from '../components/ErrorMessage'
import { ProviderDecisionCard } from '../components/ProviderDecisionCard'
import { useContactConsent } from '../hooks/useContactConsent'
import { Stars } from '../components/profile/RecentRatings'
import { ReportBlockActions } from '../components/trust/ReportBlockActions'
import type {
  BlockedPerson,
  CheckinKind,
  ConnectionRequestResponse,
  ContactRevealResponse,
  EngagementResponse,
  ParticipantCardResponse,
  RatingResponse,
} from '../types/api'

/** The engagement lifecycle, in the order a real tutoring relationship moves through it. */
const CHECKINS: { kind: CheckinKind; label: string }[] = [
  { kind: 'SCHEDULED', label: 'First session scheduled' },
  { kind: 'SESSION_HELD', label: 'Session happened' },
  { kind: 'COMPLETED', label: 'Mark completed' },
]
const NEGATIVE_CHECKINS: { kind: CheckinKind; label: string }[] = [
  { kind: 'NO_SHOW', label: 'No show' },
  { kind: 'STOPPED', label: 'Stopped' },
]

const STATUS_TONE: Record<string, string> = {
  ACCEPTED: 'bg-emerald-100 text-emerald-700',
  PENDING: 'bg-sun-400/20 text-amber-800',
  AWAITING_GUARDIAN: 'bg-sun-400/20 text-amber-800',
  REJECTED: 'bg-red-100 text-red-700',
  EXPIRED: 'bg-ink-100 text-ink-500',
  CANCELLED: 'bg-ink-100 text-ink-500',
}

/** `status.toLowerCase()` would render "awaiting_guardian" at the user. */
const STATUS_LABEL: Record<string, string> = {
  ACCEPTED: 'Connected',
  AWAITING_GUARDIAN: 'Waiting for parent',
}

export function ConnectionsPage() {
  const { profile } = useAuth()
  const consent = useContactConsent()
  const [connections, setConnections] = useState<ConnectionRequestResponse[]>([])
  const [cards, setCards] = useState<Record<string, ParticipantCardResponse>>({})
  const [ratings, setRatings] = useState<Record<string, RatingResponse[]>>({})
  const [engagements, setEngagements] = useState<EngagementResponse[]>([])
  const [contacts, setContacts] = useState<Record<string, ContactRevealResponse>>({})
  const [ratingFor, setRatingFor] = useState<ConnectionRequestResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  // Pending = a connection request nobody has answered yet, or a lapsed engagement someone has
  // asked to pick back up and is waiting on the other side for - both are "something is waiting
  // on a decision", the same shape whether that decision is the first one or a revival of it.
  // Active = accepted and still going. Expired = lapsed with nobody currently asking to revive it.
  const [tab, setTab] = useState<'pending' | 'active' | 'expired'>('active')
  const [revivalBusy, setRevivalBusy] = useState<string | null>(null)
  // Bumped after a block from a row, so the "Blocked people" list below picks it up.
  const [blocksVersion, setBlocksVersion] = useState(0)

  const me = profile?.id
  const other = useCallback(
    (c: ConnectionRequestResponse) =>
      c.senderParticipantId === me ? c.receiverParticipantId : c.senderParticipantId,
    [me],
  )

  const load = useCallback(() => {
    if (!me) return
    Promise.all([
      connectionsApi.listOwn().catch(() => [] as ConnectionRequestResponse[]),
      engagementsApi.listOwn().catch(() => [] as EngagementResponse[]),
    ])
      .then(async ([list, engs]) => {
        setConnections(list)
        setEngagements(engs)

        const otherIds = Array.from(new Set(list.map(other)))
        const [cardResults, ratingResults] = await Promise.all([
          Promise.all(otherIds.map((id) => profileApi.getCard(id).catch(() => null))),
          Promise.all(otherIds.map((id) => ratingsApi.listForParticipant(id).catch(() => []))),
        ])

        const cardMap: Record<string, ParticipantCardResponse> = {}
        const ratingMap: Record<string, RatingResponse[]> = {}
        otherIds.forEach((id, i) => {
          const card = cardResults[i]
          if (card) cardMap[id] = card
          ratingMap[id] = ratingResults[i]
        })
        setCards(cardMap)
        setRatings(ratingMap)
      })
      .catch((err) => setError(apiErrorMessage(err, 'Could not load your connections')))
      .finally(() => setLoading(false))
  }, [me, other])

  useEffect(load, [load])

  async function respond(c: ConnectionRequestResponse, accept: boolean) {
    setError(null)
    // Accepting is what releases both people's contact details, so the accepter agrees first - but
    // only on a request that runs through the network; one that lives only here shares nothing.
    // Declining shares nothing and needs no agreement.
    let contactConsent: boolean | undefined
    if (accept && c.sharesContactDetails) {
      // The sender is the other party; they are the provider when the provider on the request is them.
      const counterparty = c.provider?.participantId === c.senderParticipantId ? 'provider' : 'learner'
      contactConsent = await consent.ask('accept', counterparty)
      if (contactConsent === false) return
    }
    try {
      await connectionsApi.respond(c.id, { accept, contactConsent })
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not answer this request'))
    }
  }

  async function showContact(c: ConnectionRequestResponse) {
    setError(null)
    try {
      const revealed = await connectionsApi.getContact(c.id)
      setContacts((prev) => ({ ...prev, [c.id]: revealed }))
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not reveal contact details'))
    }
  }

  /** Creating the engagement is implicit: the first check-in opens it if it does not exist yet. */
  async function checkin(c: ConnectionRequestResponse, kind: CheckinKind) {
    setError(null)
    try {
      let engagement = engagements.find((e) => e.requestId === c.id)
      if (!engagement) {
        engagement = await engagementsApi.create({ requestId: c.id })
      }
      await engagementsApi.checkin(engagement.id, { kind })
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this engagement'))
    }
  }

  /** Ask to pick a lapsed engagement back up, accept, or decline someone else's ask. */
  async function respondToRevival(engagement: EngagementResponse, action: 'request' | 'accept' | 'decline') {
    setError(null)
    setRevivalBusy(engagement.id)
    try {
      if (action === 'request') await engagementsApi.revive(engagement.id)
      else await engagementsApi.respondToRevival(engagement.id, action === 'accept')
      load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not update this engagement'))
    } finally {
      setRevivalBusy(null)
    }
  }

  if (loading) return <div className="text-ink-500">Loading your connections...</div>

  const isSupply = profile?.persona === 'SUPPLY'
  const isExpired = (e: EngagementResponse | undefined): boolean => e?.status === 'LAPSED'
  /** A lapsed engagement someone has already asked to revive - waiting on a decision, same as a
   *  fresh request, so it belongs with those rather than sitting among the dormant expired ones. */
  const isRevivalPending = (e: EngagementResponse | undefined): boolean =>
    isExpired(e) && e?.revivalRequestedByPersonaId != null
  const bucketOf = (c: ConnectionRequestResponse, e: EngagementResponse | undefined): 'pending' | 'active' | 'expired' => {
    if (c.status === 'PENDING' || c.status === 'AWAITING_GUARDIAN' || isRevivalPending(e)) return 'pending'
    if (isExpired(e)) return 'expired'
    return 'active'
  }
  const visibleConnections = connections.filter(
    (c) => bucketOf(c, engagements.find((e) => e.requestId === c.id)) === tab,
  )
  const countOf = (bucket: 'pending' | 'active' | 'expired') =>
    connections.filter((c) => bucketOf(c, engagements.find((e) => e.requestId === c.id)) === bucket).length
  const pendingCount = countOf('pending')
  const activeCount = countOf('active')
  const expiredCount = countOf('expired')

  return (
    <div>
      {consent.dialog}
      <h1 className="flex items-center gap-2 text-xl font-bold text-ink-900">
        <Users size={18} className="text-sun-500" /> Connections
      </h1>
      <p className="text-sm text-ink-500">
        {isSupply
          ? 'Answer requests from learners, keep track of each engagement, and rate learners you taught.'
          : 'Accept requests, rate your tutors, and grow your learning circle.'}
      </p>

      <div className="mt-4">
        <ErrorMessage message={error} />
      </div>

      <h2 className="mt-4 font-bold text-ink-900">Your connections</h2>

      <div className="mt-2 flex gap-1.5">
        <button
          onClick={() => setTab('pending')}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
            tab === 'pending' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
          }`}
        >
          Pending ({pendingCount})
        </button>
        <button
          onClick={() => setTab('active')}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
            tab === 'active' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
          }`}
        >
          Active ({activeCount})
        </button>
        <button
          onClick={() => setTab('expired')}
          className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
            tab === 'expired' ? 'bg-ink-900 text-white' : 'bg-ink-100 text-ink-600 hover:bg-ink-200'
          }`}
        >
          Expired ({expiredCount})
        </button>
      </div>

      {visibleConnections.length === 0 ? (
        <div className="card-pad mt-3 py-10 text-center text-sm text-ink-500">
          {tab === 'pending'
            ? 'Nothing waiting on a decision right now.'
            : tab === 'expired'
              ? 'Nothing here - an engagement moves to Expired after 15 days with no activity.'
              : pendingCount > 0 || expiredCount > 0
                // Something exists, just not here - saying "no connection requests yet" would be
                // false, and would read as an invitation to send one again rather than "check the
                // other tabs".
                ? 'No active connections right now - check Pending or Expired.'
                : 'No connection requests yet. Find someone on the YellowDots Map to get started.'}
        </div>
      ) : (
        <div className="mt-3 space-y-3">
          {visibleConnections.map((c) => {
            const otherId = other(c)
            const card = cards[otherId]
            const theirRatings = ratings[otherId] ?? []
            const average = theirRatings.length
              ? theirRatings.reduce((s, r) => s + r.rating, 0) / theirRatings.length
              : null
            const engagement = engagements.find((e) => e.requestId === c.id)
            const iAmReceiver = c.receiverParticipantId === me
            const contact = contacts[c.id]

            return (
              <div key={c.id} className="card-pad">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="break-words font-semibold text-ink-900">
                      {card?.fullName ?? 'YellowDots participant'}
                    </div>
                    <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-ink-500">
                      <span>{card ? labelFor(card.entityKind) : ''}</span>
                      {average != null && (
                        <span className="flex items-center gap-1">
                          <Stars rating={average} size={11} /> {average.toFixed(1)} ({theirRatings.length})
                        </span>
                      )}
                      <span>
                        {iAmReceiver ? 'they requested' : 'you requested'} &middot;{' '}
                        {new Date(c.sentAt).toLocaleDateString()}
                      </span>
                    </div>
                    {c.aboutCategory && (
                      <span className="pill mt-2 bg-ink-100 text-ink-700">{c.aboutCategory}</span>
                    )}
                    {/* What they offer and how they are rated, beside the Accept button rather
                        than a click away on their profile. */}
                    {c.provider && <ProviderDecisionCard provider={c.provider} />}
                  </div>

                  <div className="flex shrink-0 flex-wrap items-center gap-2">
                    <span className={`pill ${STATUS_TONE[c.status] ?? 'bg-ink-100 text-ink-600'}`}>
                      {STATUS_LABEL[c.status] ?? c.status.toLowerCase()}
                    </span>

                    {c.status === 'PENDING' && iAmReceiver && (
                      <>
                        <button onClick={() => respond(c, true)} className="btn-sun !px-3 !py-1.5 text-xs">
                          <Check size={13} /> Accept
                        </button>
                        <button onClick={() => respond(c, false)} className="btn-ghost !px-3 !py-1.5 text-xs">
                          <X size={13} /> Decline
                        </button>
                      </>
                    )}

                    {c.status === 'ACCEPTED' && !contact && (
                      <button onClick={() => showContact(c)} className="btn-ghost !px-3 !py-1.5 text-xs">
                        <Phone size={13} /> Show contact
                      </button>
                    )}
                    {c.status === 'ACCEPTED' && (
                      <button onClick={() => setRatingFor(c)} className="btn-ghost !px-3 !py-1.5 text-xs">
                        <Star size={13} /> Rate
                      </button>
                    )}
                    {/* Last and quiet: a safety tool, not one of the row's actions. After a block
                        the list reloads, and so does "Blocked people" below. */}
                    <ReportBlockActions
                      participantId={otherId}
                      name={card?.fullName ?? 'this person'}
                      connectionRequestId={c.id}
                      onBlocked={() => {
                        load()
                        setBlocksVersion((v) => v + 1)
                      }}
                    />
                  </div>
                </div>

                {contact && (
                  <div className="mt-3 rounded-xl border border-ink-200 bg-ink-50 px-3 py-2 text-sm">
                    {/* The name arrives with the contact details, not before: it is PII on the
                        same footing, and until every party agreed the card showed initials. */}
                    <div className="break-words">
                      <span className="text-ink-500">Name</span>{' '}
                      <span className="font-semibold text-ink-900">{contact.fullName}</span>
                    </div>
                    <div className="break-words">
                      <span className="text-ink-500">Mobile</span>{' '}
                      <span className="font-semibold text-ink-900">{contact.phone}</span>
                    </div>
                    <div className="break-words">
                      <span className="text-ink-500">Email</span>{' '}
                      <span className="font-semibold text-ink-900">{contact.email}</span>
                    </div>
                    <p className="mt-1 text-xs text-ink-400">
                      This reveal is recorded and visible to both of you under Trust &amp; Safety.
                    </p>
                  </div>
                )}

                {c.status === 'ACCEPTED' && engagement && engagement.status === 'LAPSED' && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ink-100 pt-3">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-ink-400">
                      Engagement
                    </span>
                    <span className="pill bg-ink-100 text-ink-500">
                      <Clock size={11} className="mr-1 inline" /> expired - no activity in 15 days
                    </span>
                    {engagement.revivalRequestedByPersonaId == null ? (
                      <button
                        onClick={() => respondToRevival(engagement, 'request')}
                        disabled={revivalBusy === engagement.id}
                        className="btn-sun !px-3 !py-1.5 text-xs"
                      >
                        <Calendar size={13} />
                        {revivalBusy === engagement.id ? 'Sending...' : 'Request a meeting'}
                      </button>
                    ) : engagement.revivalRequestedByPersonaId === me ? (
                      <span className="pill bg-sun-400/20 text-amber-800">
                        Waiting for {card?.fullName ?? 'them'} to accept
                      </span>
                    ) : (
                      <>
                        <span className="text-xs text-ink-600">
                          {card?.fullName ?? 'They'} asked to meet again -
                        </span>
                        <button
                          onClick={() => respondToRevival(engagement, 'accept')}
                          disabled={revivalBusy === engagement.id}
                          className="btn-sun !px-3 !py-1.5 text-xs"
                        >
                          <Check size={13} /> Accept
                        </button>
                        <button
                          onClick={() => respondToRevival(engagement, 'decline')}
                          disabled={revivalBusy === engagement.id}
                          className="btn-ghost !px-3 !py-1.5 text-xs"
                        >
                          <X size={13} /> Decline
                        </button>
                      </>
                    )}
                  </div>
                )}
                {c.status === 'ACCEPTED' && !isExpired(engagement) && (
                  <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-ink-100 pt-3">
                    <span className="text-[10px] font-bold uppercase tracking-wide text-ink-400">
                      Engagement
                    </span>
                    <span className="pill bg-sun-400/20 text-amber-800">
                      {engagement ? engagement.status.replace(/_/g, ' ').toLowerCase() : 'Not started'}
                    </span>
                    {CHECKINS.map((k) => (
                      <button
                        key={k.kind}
                        onClick={() => checkin(c, k.kind)}
                        className="btn-ghost !px-3 !py-1.5 text-xs"
                      >
                        {k.label}
                      </button>
                    ))}
                    {NEGATIVE_CHECKINS.map((k) => (
                      <button
                        key={k.kind}
                        onClick={() => checkin(c, k.kind)}
                        className="rounded-xl px-2 py-1.5 text-xs text-ink-500 transition hover:bg-ink-100"
                      >
                        {k.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      <BlockedPeople version={blocksVersion} onUnblocked={load} />

      {ratingFor && (
        <RateDialog
          connection={ratingFor}
          rateeId={other(ratingFor)}
          rateeName={cards[other(ratingFor)]?.fullName ?? 'this participant'}
          onClose={() => setRatingFor(null)}
          onSaved={() => {
            setRatingFor(null)
            load()
          }}
        />
      )}
    </div>
  )
}

function labelFor(entityKind: string): string {
  if (entityKind === 'TUTOR') return 'Tutor / Coach'
  if (entityKind === 'CENTRE') return 'Centre'
  if (entityKind === 'COUNSELLOR') return 'Counsellor'
  if (entityKind === 'GUARDIAN') return 'Parent / Guardian'
  return 'Learner'
}

/**
 * Everyone this person has blocked, with an Unblock beside each. Small on purpose: it sits at the
 * bottom of Connections and most people will never have anyone in it.
 */
function BlockedPeople({ version, onUnblocked }: { version: number; onUnblocked: () => void }) {
  const [blocked, setBlocked] = useState<BlockedPerson[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)

  const load = useCallback(() => {
    trustApi
      .listBlocks()
      .then(setBlocked)
      .catch((err) => setError(apiErrorMessage(err, 'Could not load the people you blocked')))
  }, [])

  useEffect(load, [load, version])

  async function unblock(person: BlockedPerson) {
    setError(null)
    setBusyId(person.participantId)
    try {
      await trustApi.unblock(person.participantId)
      load()
      onUnblocked()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not unblock this person'))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <section className="mt-8 border-t border-ink-200 pt-4">
      <h2 className="flex items-center gap-1.5 text-sm font-bold text-ink-700">
        <Ban size={14} className="text-ink-400" /> Blocked people
      </h2>
      <div className="mt-2">
        <ErrorMessage message={error} />
      </div>
      {blocked == null ? (
        !error && <p className="mt-1 text-xs text-ink-400">Loading...</p>
      ) : blocked.length === 0 ? (
        <p className="mt-1 text-xs text-ink-500">You have not blocked anyone.</p>
      ) : (
        <ul className="mt-2 divide-y divide-ink-100 rounded-xl border border-ink-200">
          {blocked.map((b) => (
            <li key={b.participantId} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
              <div className="min-w-0">
                <div className="break-words text-sm font-semibold text-ink-900">{b.fullName}</div>
                <div className="text-xs text-ink-500">
                  {labelFor(b.entityKind)}
                  {b.blockedAt && <> &middot; blocked {new Date(b.blockedAt).toLocaleDateString()}</>}
                </div>
              </div>
              <button
                onClick={() => unblock(b)}
                disabled={busyId === b.participantId}
                className="btn-ghost !px-3 !py-1.5 text-xs"
              >
                {busyId === b.participantId ? 'Unblocking...' : 'Unblock'}
              </button>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

function RateDialog({
  connection,
  rateeId,
  rateeName,
  onClose,
  onSaved,
}: {
  connection: ConnectionRequestResponse
  rateeId: string
  rateeName: string
  onClose: () => void
  onSaved: () => void
}) {
  const [rating, setRating] = useState(5)
  const [comments, setComments] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  async function save() {
    setError(null)
    setSaving(true)
    try {
      await ratingsApi.create({
        rateeParticipantId: rateeId,
        connectionRequestId: connection.id,
        rating,
        comments: comments.trim() || undefined,
      })
      onSaved()
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not save your rating'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[1100] grid place-items-center overflow-y-auto bg-ink-900/40 p-4">
      <div className="card-pad max-h-[90vh] w-full max-w-sm overflow-y-auto bg-white">
        <div className="flex items-start justify-between gap-3">
          <h2 className="font-bold text-ink-900">Rate {rateeName}</h2>
          <button onClick={onClose} aria-label="Close" className="text-ink-400 hover:text-ink-700">
            <X size={18} />
          </button>
        </div>

        <div className="mt-3 flex gap-1.5">
          {[1, 2, 3, 4, 5].map((n) => (
            <button
              key={n}
              onClick={() => setRating(n)}
              aria-label={`${n} star${n === 1 ? '' : 's'}`}
              className="p-0.5"
            >
              <Star size={24} className={n <= rating ? 'fill-sun-500 text-sun-500' : 'text-ink-200'} />
            </button>
          ))}
        </div>

        <label className="mt-3 block">
          <span className="label">Comments</span>
          <textarea
            value={comments}
            onChange={(e) => setComments(e.target.value)}
            rows={3}
            className="input"
            placeholder="What went well?"
          />
        </label>

        <div className="mt-2">
          <ErrorMessage message={error} />
        </div>

        <div className="mt-3 flex flex-wrap justify-end gap-2">
          <button onClick={onClose} className="btn-ghost">Cancel</button>
          <button onClick={save} disabled={saving} className="btn-sun">
            {saving ? 'Saving...' : 'Submit rating'}
          </button>
        </div>
      </div>
    </div>
  )
}
